'use client';

import * as THREE from 'three';

import { collectActorParentEdgesFromTree, type SceneTreeActorNodeLike } from './sceneTreeActorEdges';

import type * as ENGINE from '@gnsx/genesys.js';

interface ActorParentingEntry {
  parentActorUuid: string;
  childActorUuid: string;
  parentRootComponentUuid: string;
  childRootComponentUuid: string;
}

export interface ActorParentingRebuildResult {
  appliedEdges: number;
  skippedEdges: number;
  errors: string[];
}

const childToEntry = new Map<string, ActorParentingEntry>();
const parentToChildren = new Map<string, Set<string>>();

const DEBUG = {
  log: false,
} as const;

function getWorldTransform(object: THREE.Object3D) {
  return {
    position: object.getWorldPosition(new THREE.Vector3()),
    quaternion: object.getWorldQuaternion(new THREE.Quaternion()),
    scale: object.getWorldScale(new THREE.Vector3()),
  };
}

function addParentToChildIndex(parentUuid: string, childUuid: string) {
  let children = parentToChildren.get(parentUuid);
  if (!children) {
    children = new Set<string>();
    parentToChildren.set(parentUuid, children);
  }
  children.add(childUuid);
}

function removeParentToChildIndex(parentUuid: string, childUuid: string) {
  const children = parentToChildren.get(parentUuid);
  if (!children) return;
  children.delete(childUuid);
  if (children.size === 0) {
    parentToChildren.delete(parentUuid);
  }
}

function setEntry(entry: ActorParentingEntry) {
  const existing = childToEntry.get(entry.childActorUuid);
  if (existing && existing.parentActorUuid !== entry.parentActorUuid) {
    removeParentToChildIndex(existing.parentActorUuid, existing.childActorUuid);
  }
  childToEntry.set(entry.childActorUuid, entry);
  addParentToChildIndex(entry.parentActorUuid, entry.childActorUuid);
}

function removeEntry(childActorUuid: string) {
  const existing = childToEntry.get(childActorUuid);
  if (!existing) return;
  removeParentToChildIndex(existing.parentActorUuid, existing.childActorUuid);
  childToEntry.delete(childActorUuid);
}

function detachRootToScenePreserveWorld(root: ENGINE.SceneComponent, scene: THREE.Scene): boolean {
  const worldTransform = getWorldTransform(root);
  root.removeFromParent();
  scene.add(root);
  root.position.copy(worldTransform.position);
  root.quaternion.copy(worldTransform.quaternion);
  root.scale.copy(worldTransform.scale);
  return true;
}

function hasParentChainCycle(object: THREE.Object3D): boolean {
  let current = object.parent;
  const visited = new Set<THREE.Object3D>();
  while (current) {
    if (visited.has(current)) return true;
    visited.add(current);
    current = current.parent;
  }
  return false;
}

function detachRootToSceneBestEffort(root: ENGINE.SceneComponent, scene: THREE.Scene): boolean {
  if (hasParentChainCycle(root)) {
    root.removeFromParent();
    scene.add(root);
    return false;
  }

  try {
    return detachRootToScenePreserveWorld(root, scene);
  } catch (error) {
    console.warn(`[actorParenting] Detaching ${root.uuid} without preserving world transform after transform update failed`, error);
    root.removeFromParent();
    scene.add(root);
    return false;
  }
}

function isObjectDescendantOf(object: THREE.Object3D, possibleAncestor: THREE.Object3D): boolean {
  let current = object.parent;
  const visited = new Set<THREE.Object3D>();
  while (current) {
    if (current === possibleAncestor) return true;
    if (visited.has(current)) return false;
    visited.add(current);
    current = current.parent;
  }
  return false;
}

function isActorAncestor(possibleAncestorUuid: string, actorUuid: string): boolean {
  let current = childToEntry.get(actorUuid)?.parentActorUuid;
  while (current) {
    if (current === possibleAncestorUuid) return true;
    current = childToEntry.get(current)?.parentActorUuid;
  }
  return false;
}

function toOutlinerNodeLike(node: unknown): SceneTreeActorNodeLike | null {
  if (!node || typeof node !== 'object') return null;
  const candidate = node as Partial<SceneTreeActorNodeLike>;
  if (
    typeof candidate.id !== 'string' ||
    (candidate.nodeType !== 'scene' && candidate.nodeType !== 'folder' && candidate.nodeType !== 'actor') ||
    !Array.isArray(candidate.children)
  ) {
    return null;
  }
  return candidate as SceneTreeActorNodeLike;
}

function getEntryDepth(entry: ActorParentingEntry): number {
  let depth = 0;
  let current = childToEntry.get(entry.parentActorUuid);
  const visited = new Set<string>();
  while (current) {
    if (visited.has(current.childActorUuid)) return depth;
    visited.add(current.childActorUuid);
    depth += 1;
    current = childToEntry.get(current.parentActorUuid);
  }
  return depth;
}

function getActorWorldAndScene(actor: ENGINE.Actor): { world: ENGINE.World; scene: THREE.Scene } | null {
  const world = actor.getWorld();
  const scene = world?.scene;
  if (!world || !scene) return null;
  return { world, scene };
}

function getRootPair(child: ENGINE.Actor, parent: ENGINE.Actor) {
  const childRoot = child.rootComponent;
  const parentRoot = parent.rootComponent;
  if (!childRoot || !parentRoot) return null;
  return { childRoot, parentRoot };
}

export const actorParenting = {
  clear() {
    childToEntry.clear();
    parentToChildren.clear();
  },
  isParented(actorOrUuid: ENGINE.Actor | string): boolean {
    const actorUuid = typeof actorOrUuid === 'string' ? actorOrUuid : actorOrUuid.uuid;
    return childToEntry.has(actorUuid);
  },
  getParentActorUuid(childActorOrUuid: ENGINE.Actor | string): string | null {
    const childActorUuid = typeof childActorOrUuid === 'string' ? childActorOrUuid : childActorOrUuid.uuid;
    return childToEntry.get(childActorUuid)?.parentActorUuid ?? null;
  },
  getParentActor(childActor: ENGINE.Actor): ENGINE.Actor | null {
    const parentUuid = this.getParentActorUuid(childActor);
    if (!parentUuid) return null;
    return childActor.getWorld()?.getActorByUuid(parentUuid) ?? null;
  },
  getChildActorUuids(parentActorOrUuid: ENGINE.Actor | string): string[] {
    const parentUuid = typeof parentActorOrUuid === 'string' ? parentActorOrUuid : parentActorOrUuid.uuid;
    return Array.from(parentToChildren.get(parentUuid) ?? []);
  },
  attachActorToParent(childActor: ENGINE.Actor, parentActor: ENGINE.Actor): boolean {
    if (childActor.uuid === parentActor.uuid) return false;
    if (isActorAncestor(childActor.uuid, parentActor.uuid)) {
      console.warn(`[actorParenting] Skipping attach (${childActor.uuid} -> ${parentActor.uuid}): would create cycle`);
      return false;
    }

    const roots = getRootPair(childActor, parentActor);
    if (!roots) return false;

    if (hasParentChainCycle(roots.childRoot)) {
      const worldAndScene = getActorWorldAndScene(childActor);
      if (!worldAndScene) return false;
      removeEntry(childActor.uuid);
      detachRootToSceneBestEffort(roots.childRoot, worldAndScene.scene);
    }

    if (isObjectDescendantOf(roots.parentRoot, roots.childRoot)) {
      const worldAndScene = getActorWorldAndScene(parentActor);
      if (!worldAndScene) return false;
      removeEntry(parentActor.uuid);
      detachRootToSceneBestEffort(roots.parentRoot, worldAndScene.scene);
    }

    // Ensure the child is detached from any previous actor parent first.
    if (this.isParented(childActor)) {
      this.detachActorFromParent(childActor);
    }

    try {
      roots.parentRoot.attach(roots.childRoot);
    } catch (error) {
      console.warn(`[actorParenting] Failed to attach child ${childActor.uuid} to parent ${parentActor.uuid}`, error);
      return false;
    }

    setEntry({
      parentActorUuid: parentActor.uuid,
      childActorUuid: childActor.uuid,
      parentRootComponentUuid: roots.parentRoot.uuid,
      childRootComponentUuid: roots.childRoot.uuid,
    });

    if (DEBUG.log) {
      console.log(`[actorParenting] attach ${childActor.uuid} -> ${parentActor.uuid}`);
    }
    return true;
  },
  detachActorFromParent(childActor: ENGINE.Actor): boolean {
    const entry = childToEntry.get(childActor.uuid);
    if (!entry) return false;

    const worldAndScene = getActorWorldAndScene(childActor);
    if (!worldAndScene) {
      removeEntry(childActor.uuid);
      return false;
    }

    const childRoot = childActor.rootComponent;
    if (!childRoot) {
      removeEntry(childActor.uuid);
      return false;
    }

    removeEntry(childActor.uuid);
    detachRootToSceneBestEffort(childRoot, worldAndScene.scene);
    return true;
  },
  /**
   * Handles actor deletion safely:
   * - children are detached to scene root (preserving world transform)
   * - links where this actor is a child are removed
   * - links where this actor is a parent are removed
   */
  handleActorRemoval(actor: ENGINE.Actor) {
    const worldAndScene = getActorWorldAndScene(actor);
    const directChildren = this.getChildActorUuids(actor);
    for (const childUuid of directChildren) {
      const childActor = worldAndScene?.world.getActorByUuid(childUuid) ?? null;
      if (!childActor?.rootComponent || !worldAndScene) {
        removeEntry(childUuid);
        continue;
      }
      removeEntry(childUuid);
      detachRootToSceneBestEffort(childActor.rootComponent, worldAndScene.scene);
    }

    removeEntry(actor.uuid);
    parentToChildren.delete(actor.uuid);
  },
  validateAndRepairAttachments(world: ENGINE.World) {
    for (const [childActorUuid, entry] of childToEntry) {
      const childActor = world.getActorByUuid(childActorUuid);
      const parentActor = world.getActorByUuid(entry.parentActorUuid);
      if (!childActor || !parentActor) {
        removeEntry(childActorUuid);
        continue;
      }

      const roots = getRootPair(childActor, parentActor);
      if (!roots) {
        removeEntry(childActorUuid);
        continue;
      }

      const rootsChanged =
        entry.childRootComponentUuid !== roots.childRoot.uuid ||
        entry.parentRootComponentUuid !== roots.parentRoot.uuid;

      const childParentMismatch = roots.childRoot.parent !== roots.parentRoot;

      if (!rootsChanged && !childParentMismatch) continue;

      try {
        roots.parentRoot.attach(roots.childRoot);
      } catch (error) {
        console.warn(`[actorParenting] Failed to repair attachment for child ${childActorUuid}`, error);
        continue;
      }

      setEntry({
        parentActorUuid: parentActor.uuid,
        childActorUuid: childActor.uuid,
        parentRootComponentUuid: roots.parentRoot.uuid,
        childRootComponentUuid: roots.childRoot.uuid,
      });
    }
  },
  detachAllParenting(world: ENGINE.World, scene: THREE.Scene, repairFirst: boolean = true): ActorParentingEntry[] {
    if (repairFirst) {
      this.validateAndRepairAttachments(world);
    }

    const entries = Array.from(childToEntry.values()).sort((a, b) => getEntryDepth(b) - getEntryDepth(a));

    for (const entry of entries) {
      const childActor = world.getActorByUuid(entry.childActorUuid);
      const childRoot = childActor?.rootComponent;
      if (!childActor || !childRoot) continue;
      detachRootToSceneBestEffort(childRoot, scene);
    }

    return entries.map(entry => ({ ...entry }));
  },
  reattachAllParenting(entries: ActorParentingEntry[], world: ENGINE.World) {
    const sortedEntries = [...entries].sort((a, b) => getEntryDepth(a) - getEntryDepth(b));

    for (const entry of sortedEntries) {
      const parentActor = world.getActorByUuid(entry.parentActorUuid);
      const childActor = world.getActorByUuid(entry.childActorUuid);
      if (!parentActor || !childActor) {
        removeEntry(entry.childActorUuid);
        continue;
      }
      this.attachActorToParent(childActor, parentActor);
    }
  },
  rebuildParentingFromTree(rootNodeUnknown: unknown, world: ENGINE.World): ActorParentingRebuildResult {
    const result: ActorParentingRebuildResult = {
      appliedEdges: 0,
      skippedEdges: 0,
      errors: [],
    };

    try {
      this.detachAllParenting(world, world.scene, false);
      this.clear();
    } catch (error) {
      result.errors.push(`[actorParenting] Failed to detach existing parenting before rebuild: ${String(error)}`);
      console.warn('[actorParenting] Failed to detach existing parenting before rebuild', error);
      return result;
    }

    const rootNode = toOutlinerNodeLike(rootNodeUnknown);
    if (!rootNode) return result;

    const edgeCollection = collectActorParentEdgesFromTree(rootNode);
    edgeCollection.skippedNodeIds.forEach(id => {
      console.warn(`[actorParenting] Skipping repeated outliner node ${id}: would create cycle`);
    });
    result.skippedEdges += edgeCollection.skippedNodeIds.length;

    for (const edge of edgeCollection.edges) {
      const childActor = world.getActorByUuid(edge.childUuid);
      if (!childActor) {
        result.skippedEdges += 1;
        result.errors.push(`[actorParenting] Actor ${edge.childUuid} missing during rebuild`);
        continue;
      }

      const parentActor = world.getActorByUuid(edge.parentUuid);
      if (!parentActor) {
        result.skippedEdges += 1;
        result.errors.push(`[actorParenting] Parent actor ${edge.parentUuid} missing for child ${edge.childUuid}`);
        continue;
      }

      try {
        const attached = this.attachActorToParent(childActor, parentActor);
        if (attached) {
          result.appliedEdges += 1;
        } else {
          result.skippedEdges += 1;
          result.errors.push(`[actorParenting] Failed to attach child ${edge.childUuid} to parent ${edge.parentUuid}`);
        }
      } catch (error) {
        result.skippedEdges += 1;
        result.errors.push(`[actorParenting] Failed to attach ${edge.childUuid} -> ${edge.parentUuid}: ${String(error)}`);
        console.warn(`[actorParenting] Failed to attach ${edge.childUuid} -> ${edge.parentUuid}`, error);
      }
    }

    return result;
  },
};
